import { Button, TextField, InputAdornment, Stack, Box } from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import SearchIcon from '@mui/icons-material/Search';
import { resetBuildingSelection, setLocation } from '../onsite-visit.slice';
import { useAppDispatch, useAppSelector } from 'src/redux-app-hooks';
import useGoogleMapsApiLoader from 'src/infrastructure/google/useGoogleMapsApiLoader';

export default function Search() {
    const { isMapLoading } = useAppSelector((state) => state.onsiteVisit);
    const dispatch = useAppDispatch();
    const [address, setAddress] = useState('');
    const [geocoder, setGeocoder] = useState<google.maps.Geocoder>();
    const loader = useGoogleMapsApiLoader();
    const { t } = useTranslation();

    useEffect(() => {
        loadGeocoder();
    }, []);

    async function loadGeocoder() {
        const { Geocoder } = (await loader.importLibrary('geocoding')) as google.maps.GeocodingLibrary;
        setGeocoder(new Geocoder());
    }

    async function handleChangeAddress(e: any) {
        if ((e.key === 'Enter' || e.type === 'click') && geocoder) {
            // `geocoder.geocode` can resolve to undefined when the Google Maps API
            // failed to bootstrap cleanly (e.g. NotLoadingAPIFromGoogleMapsError),
            // so guard the response before dereferencing `results`.
            const response = await geocoder.geocode({ address }).catch(() => undefined);
            if (response && response.results.length) {
                const result = response.results[0];
                dispatch(
                    setLocation({
                        lat: result.geometry.location.lat(),
                        lng: result.geometry.location.lng(),
                        address: result.formatted_address,
                    }),
                );
                dispatch(resetBuildingSelection());
            }
        }
    }

    return (
        <Box sx={{ width: '100%' }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ alignItems: 'stretch' }}>
                <TextField
                    placeholder={t('Search')}
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    onKeyDown={handleChangeAddress}
                    fullWidth
                    size="small"
                    disabled={isMapLoading}
                    slotProps={{
                        input: {
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" />
                                </InputAdornment>
                            ),
                        },
                    }}
                />
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleChangeAddress}
                    disabled={isMapLoading}
                    size="small" // makes it match the input height
                    sx={{ minWidth: 100 }}
                    startIcon={<SearchIcon />}
                >
                    {t('Search')}
                </Button>
            </Stack>
        </Box>
    );
}
